import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { IPasswordValidatorResult } from '../../../utils/constants/interfaces';

const usePasswordResetForm = (
  token: string,
  userId: string,
  confirmPasswordResetEndpoint?: string
) => {
  const api = useApi();
  const [validPassword, setValidPassword] = useState<boolean>(false);
  const [validToken, setValidToken] = useState<boolean | null>(null);
  const [resetResult, setResetResult] = useState<boolean | null>(null);
  const [resetRequest, setResetRequest] = useState({
    passwordResetToken: token,
    userId: userId,
    password: ''
  });
  const [validatorResult, setValidatorResult] = useState<IPasswordValidatorResult>({
    currentPassword: '',
    newPassword: '',
    isValid: false
  });

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await resetPasswordAsync();
  };

  const passwordCallback = useCallback((value: IPasswordValidatorResult): void => {
    setValidatorResult((state) => ({
      ...state,
      currentPassword: value.currentPassword,
      newPassword: value.newPassword,
      isValid: value.isValid
    }));
    setValidPassword(value.isValid);
    setResetRequest((state) => ({ ...state, password: value.newPassword }));
  }, []);

  const validateRequestToken = useCallback(async (): Promise<void> => {
    const apiResponse = await api.post<boolean>(
      '/auth/validate-password-reset-token',
      JSON.stringify({
        passwordResetToken: token,
        userId: userId
      })
    );
    if (!apiResponse || !apiResponse.response || apiResponse.status.statusCode !== 200) {
      setValidToken(false);
    } else {
      setValidToken(apiResponse.response ?? false);
    }
  }, [api, token, userId]);

  const resetPasswordAsync = async (): Promise<void> => {
    const apiResponse = await api.post<boolean>(
      typeof confirmPasswordResetEndpoint === 'undefined' ||
        Helpers.isNullOrWhiteSpace(confirmPasswordResetEndpoint)
        ? '/auth/confirm-password-reset'
        : confirmPasswordResetEndpoint,
      JSON.stringify(resetRequest)
    );
    if (!apiResponse || !apiResponse.response || apiResponse.status.statusCode !== 200) {
      setResetResult(false);
    } else {
      setResetResult(apiResponse.response);
    }
  };

  useEffect(() => {
    void validateRequestToken();
  }, [validateRequestToken]);

  return {
    validPassword,
    validToken,
    resetResult,
    handleSubmit,
    validateRequestToken,
    passwordCallback,
    validatorResult
  };
};

export default usePasswordResetForm;
